import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Box,
  IconButton,
  MenuItem,
  FormControl,
  Chip,
  Tooltip,
  Typography,
  InputAdornment,
  TablePagination,
  Card,
  Avatar,
  CircularProgress,
} from '@mui/material';
import BookingModal from './BookingModal';
import { 
  Delete, 
  Search, 
  FilterList, 
  FileDownload, 
  Add, 
  Circle, 
  Phone, 
  DirectionsCar,
  LocalParking,
  EuroSymbol,
  CreditCard,
  Event,
  EventAvailable,
  CalendarToday,
  ArrowForward,
  Refresh,
  LockOpen,
  VpnKey
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { styled, alpha } from '@mui/material/styles';
import axios from 'axios';
// ...existing code...

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  overflow: 'auto',
  maxHeight: 'calc(100vh - 280px)',
  // Aggiungiamo scroll orizzontale e verticale
  overflowX: 'auto',
  overflowY: 'auto',
  // Miglioriamo l'esperienza di scroll su mobile
  '-webkit-overflow-scrolling': 'touch',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: alpha(theme.palette.primary.main, 0.03),
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.07),
    transition: 'background-color 0.2s ease',
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
}));

const SearchWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
  },
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const StatusChip = styled(Chip)(({ theme, statuscolor }) => ({
  backgroundColor: alpha(statuscolor, 0.12),
  color: statuscolor,
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: statuscolor,
  },
}));

// Funzione che restituisce il colore del pallino in base allo stato
const getStatusColor = (status) => {
  switch (status) {
    case 'In attesa':
      return '#FF9800'; // Arancione
    case 'In corso':
      return '#4CAF50'; // Verde
    case 'Scaduta':
      return '#F44336'; // Rosso
    default:
      return '#9E9E9E'; // Grigio
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'In attesa':
      return <EventAvailable style={{ fontSize: '0.8rem' }} />;
    case 'In corso':
      return <Circle style={{ fontSize: '0.8rem' }} />;
    case 'Scaduta':
      return <Event style={{ fontSize: '0.8rem' }} />;
    default:
      return <Circle style={{ fontSize: '0.8rem' }} />;
  }
};

const BookingTable = ({
  bookings,
  stops,
  getBookings,
  onAdd,
  onEdit,
  onDelete,
  searchTerm,
  setSearchTerm,
  users,
  addUser,
  addVehicle,
  pitches,
  vehicles,
  getVehicles,
  onOpenGate,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [filteredBookings, setFilteredBookings] = useState(bookings);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [filterStatus, setFilterStatus] = useState('active');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState({ field: 'start', direction: 'asc' });
  const [openingGateId, setOpeningGateId] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    filterAndSortBookings();
  }, [searchTerm, bookings, filterStatus, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questa prenotazione?')) {
      onDelete(id);
    }
  };

  const handleModalClose = () => {
    setCurrentBooking(null);
    setOpenModal(false);
  };

  const handleSort = (field) => {
    setSortBy(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      await getBookings();
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortBookings = () => {
    const lowercasedFilter = searchTerm.toLowerCase();

    // Filter bookings
    let filtered = bookings.filter((booking) => {
      const matchesSearch =
        booking.users?.firstName?.toLowerCase().includes(lowercasedFilter) ||
        booking.users?.lastName?.toLowerCase().includes(lowercasedFilter) ||
        booking.users?.phone?.toLowerCase().includes(lowercasedFilter) ||
        booking.vehicles?.licensePlate
          ?.toLowerCase()
          .includes(lowercasedFilter) ||
        booking.restareas?.friendlyName
          ?.toLowerCase()
          .includes(lowercasedFilter) ||
        formatDate(booking.start)?.toLowerCase().includes(lowercasedFilter) ||
        formatDate(booking.end)?.toLowerCase().includes(lowercasedFilter) ||
        String(booking.price)?.toLowerCase().includes(lowercasedFilter);

      // Filter by status
      const status = getStatus(booking.start, booking.end);
      const matchesStatus =
        filterStatus === 'all' ||
        (filterStatus === 'active' && (status === 'In corso' || status === 'In attesa')) ||
        (filterStatus === 'expired' && status === 'Scaduta');

      return matchesSearch && matchesStatus;
    });

    // Sort bookings
    filtered.sort((a, b) => {
      let valueA, valueB;
      
      // Determine values based on sort field
      switch (sortBy.field) {
        case 'name':
          valueA = a.users?.firstName || '';
          valueB = b.users?.firstName || '';
          break;
        case 'start':
          valueA = a.start;
          valueB = b.start;
          break;
        case 'end':
          valueA = a.end;
          valueB = b.end;
          break;
        case 'price':
          valueA = a.price;
          valueB = b.price;
          break;
        default:
          valueA = a.start;
          valueB = b.start;
          break;
      }
      
      // Sort in ascending or descending order
      if (sortBy.direction === 'asc') {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });

    setFilteredBookings(filtered);
    // Reset to first page when filters change
    setPage(0);
  };

  const formatDate = (unixTime) => {
    const date = new Date(unixTime * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Funzione per determinare lo stato della prenotazione
  const getStatus = (startTime, endTime) => {
    const now = Date.now() / 1000;
    if (startTime > now) {
      return 'In attesa';
    } else if (endTime > now) {
      return 'In corso';
    } else {
      return 'Scaduta';
    }
  };

  // Funzione per calcolare il numero di giorni della sosta
  const calculateDays = (startTime, endTime) => {
    const startDate = new Date(startTime * 1000);
    const endDate = new Date(endTime * 1000);
    const differenceInTime = endDate - startDate;
    return Math.ceil(differenceInTime / (1000 * 60 * 60 * 24));
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'bonifico':
        return 'Bonifico';
      case 'contanti':
        return 'Contanti';
      case 'carta':
        return 'Carta di Credito';
      default:
        return 'Non Specificato';
    }
  };
  
  const getPaymentMethodColor = (method) => {
    switch (method) {
      case 'bonifico':
        return 'primary';
      case 'contanti':
        return 'success';
      case 'carta':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      bookings.map((booking) => ({
        Nome: booking.users.firstName,
        Cognome: booking.users.lastName,
        Telefono: booking.users.phone,
        Targa: booking.vehicles.licensePlate,
        Piazzola: booking.restareas.friendlyName,
        'Inizio Sosta': formatDate(booking.start),
        'Fine Sosta': formatDate(booking.end),
        Prezzo: booking.price,
        Stato: getStatus(booking.start, booking.end),
        'Giorni Sosta': calculateDays(booking.start, booking.end),
        Pagamento: getPaymentMethodLabel(booking.paymentType),
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bookings');
    XLSX.writeFile(wb, 'prenotazioni_camper.xlsx');
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // handleOpenGate ora viene passato come prop onOpenGate
  
  // Calculate pagination
  const paginatedBookings = rowsPerPage > 0
    ? filteredBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : filteredBookings;

  // Statistics
  const totalBookings = bookings.length;
  const activeBookings = bookings.filter(b => {
    const status = getStatus(b.start, b.end);
    return status === 'In corso' || status === 'In attesa';
  }).length;
  const waitingBookings = bookings.filter(b => getStatus(b.start, b.end) === 'In attesa').length;
  const totalRevenue = bookings.reduce((sum, booking) => sum + booking.price, 0);

  return (
    <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
      <Box sx={{ p: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03) }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          pb={2}
        >
          {/* Left side buttons */}
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => setOpenModal(true)}
              sx={{ px: 2 }}
            >
              Nuova Prenotazione
            </Button>
            <Tooltip title="Esporta in Excel">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownload />}
                onClick={exportToExcel}
              >
                Esporta
              </Button>
            </Tooltip>
            <Tooltip title="Aggiorna dati">
              <IconButton 
                color="primary" 
                onClick={refreshData}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : <Refresh />}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Right side filters */}
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <TextField
                select
                size="small"
                label="Filtro Stato"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterList fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="all">Tutti gli stati</MenuItem>
                <MenuItem value="active">In corso / In attesa</MenuItem>
                <MenuItem value="expired">Scadute</MenuItem>
              </TextField>
            </FormControl>
            
            <SearchWrapper>
              <TextField
                size="small"
                placeholder="Cerca prenotazione..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none',
                    }
                  }
                }}
              />
            </SearchWrapper>
          </Box>
        </Box>
        
        {/* Stats summary */}
        <Box 
          display="flex" 
          gap={3} 
          flexWrap="wrap"
          pt={1}
          borderTop={1} 
          borderColor="divider"
        >
          <Typography variant="body2" color="text.secondary">
            <b>Totale:</b> {totalBookings} prenotazioni
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>Attive:</b> {activeBookings} prenotazioni
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <b>In attesa:</b> {waitingBookings} prenotazioni
          </Typography>
          <Typography variant="body2" color="success.main">
            <b>Incasso totale:</b> € {totalRevenue.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      <StyledTableContainer>
        <Table stickyHeader sx={{ minWidth: 1200 }}>
          <StyledTableHead>
            <TableRow>
              <TableCell>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleSort('name')}
                >
                  Cliente
                  {sortBy.field === 'name' && (
                    <ArrowForward 
                      fontSize="small" 
                      sx={{ 
                        ml: 0.5, 
                        transform: sortBy.direction === 'asc' ? 'rotate(-90deg)' : 'rotate(90deg)',
                        opacity: 0.7,
                        fontSize: '1rem'
                      }} 
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>Contatto</TableCell>
              <TableCell>Veicolo</TableCell>
              <TableCell>Piazzola</TableCell>
              <TableCell>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleSort('start')}
                >
                  Periodo
                  {sortBy.field === 'start' && (
                    <ArrowForward 
                      fontSize="small" 
                      sx={{ 
                        ml: 0.5, 
                        transform: sortBy.direction === 'asc' ? 'rotate(-90deg)' : 'rotate(90deg)',
                        opacity: 0.7,
                        fontSize: '1rem'
                      }} 
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleSort('price')}
                >
                  Prezzo
                  {sortBy.field === 'price' && (
                    <ArrowForward 
                      fontSize="small" 
                      sx={{ 
                        ml: 0.5, 
                        transform: sortBy.direction === 'asc' ? 'rotate(-90deg)' : 'rotate(90deg)',
                        opacity: 0.7,
                        fontSize: '1rem'
                      }} 
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell align="center">Giorni</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Pagamento</TableCell>
              <TableCell align="center">Azioni</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {paginatedBookings.length > 0 ? (
              paginatedBookings.map((booking) => {
                const status = getStatus(booking.start, booking.end);
                return (
                  <StyledTableRow key={booking.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                          }}
                        >
                          {booking.users?.firstName?.[0] || ''}{booking.users?.lastName?.[0] || ''}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {booking.users?.firstName} {booking.users?.lastName}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">{booking.users?.phone}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<DirectionsCar fontSize="small" />}
                        label={booking.vehicles?.licensePlate || 'N/A'}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocalParking fontSize="small" color="primary" />
                        <Typography variant="body2">
                          {booking.restareas?.friendlyName || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                          <CalendarToday fontSize="small" color="primary" /> 
                          {formatDate(booking.start)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          → {formatDate(booking.end)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight={600} 
                        color="primary.main"
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                      >
                        <EuroSymbol fontSize="small" /> {booking.price}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={calculateDays(booking.start, booking.end)}
                        color="default"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        icon={getStatusIcon(status)}
                        label={status}
                        variant="outlined"
                        size="small"
                        statuscolor={getStatusColor(status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<CreditCard fontSize="small" />}
                        label={getPaymentMethodLabel(booking.paymentType)}
                        size="small"
                        color={getPaymentMethodColor(booking.paymentType)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {(status === 'In corso' || status === 'In attesa') && (
                        <Tooltip title="Apri cancello">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setOpeningGateId(booking.id);
                                Promise.resolve(onOpenGate && onOpenGate(booking.id)).finally(() => setOpeningGateId(null));
                              }}
                              disabled={openingGateId === booking.id}
                              sx={{ mr: 0.5 }}
                            >
                              {openingGateId === booking.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <VpnKey fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Elimina">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDelete(booking.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </StyledTableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Nessuna prenotazione trovata
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Prova a cambiare i filtri o a creare una nuova prenotazione
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredBookings.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Righe per pagina:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
      />
      
      <BookingModal
        open={openModal}
        booking={currentBooking}
        users={users}
        addUser={addUser}
        addVehicle={addVehicle}
        pitches={pitches}
        vehicles={vehicles}
        stops={stops}
        onClose={handleModalClose}
        onSave={async (booking) => {
          if (currentBooking) {
            await onEdit(booking); // Modifica la prenotazione esistente
          } else {
            await onAdd(booking); // Aggiungi una nuova prenotazione
          }
          await getBookings(); // Ricarica l'elenco delle prenotazioni dopo l'operazione
          await getVehicles(); // Ricarica l'elenco dei veicoli dopo l'operazione
          handleModalClose(); // Chiudi la modale
        }}
      />
    </Card>
  );
};

export default BookingTable;